import { configServiceModule } from '../config-service/config-service';
import { accountServiceModule } from '../account-service/account-service';
import { uiRouterStateModule } from '../../vendor/ui-router-module';
import { timerHelperModule } from '../../helpers/timer-helper';
import { dialogComponentModule } from '../../components/dialog/dialog';
import { ucFirst } from '../../utils/string-utils';

export const productMaintenanceServiceModule = angular
  .module('Q.services.product-maintenance-service', [
    configServiceModule,
    accountServiceModule,
    uiRouterStateModule,
    timerHelperModule,
    dialogComponentModule,
  ])

  .run(function (productMaintenanceService) {
    productMaintenanceService.init();
  })

  .factory(
    'productMaintenanceService',
    function (
      configService,
      accountService,
      $state,
      $q,
      $injector,
      timerHelper,
      $filter,
      dialogService,
    ) {
      const precheck = (action) =>
        configService
          .load()
          .then(accountService.whenReady)
          .then(action, action) // ignore any loading errors
          .then((ok) => (ok ? $q.resolve(ok) : $q.reject(ok)));

      let _event;

      const WARNING_TIMES = {
        INFO: 4 * 60 * 60 * 1000, // 4 hours before event
        WARNING: 60 * 60 * 1000, // 1 hour before event
        DANGER: 15 * 60 * 1000, // 15 minutes
        DANGER2: 5 * 60 * 1000, // 5 minutes
      };

      const svc = {
        get active() {
          const event = configService.config.productMaintenance;
          if (event && event !== _event) {
            event.startTime = new Date(event.startTime);
            _event = event;
          }
          return event;
        },
        canAccess() {
          const active = svc.active;
          return (
            !active || svc.isStaff() || Date.now() < active.startTime.getTime()
          );
        },
        checkAccess() {
          return precheck(svc.canAccess);
        },
        // if assessment is passed in, we'll use the estimated time for candidate lockouts
        canLogin(assessment) {
          const active = svc.active;
          if (active) {
            const startTime = active.startTime.getTime();
            const minute = 60000;
            const now = Date.now();
            if (
              accountService.modelName === 'TeamMember' &&
              active.preventTeamLoginMinutes
            ) {
              return now < startTime - minute * active.preventTeamLoginMinutes;
            } else if (
              accountService.modelName === 'Candidate' &&
              active.preventCandidateLogin
            ) {
              // always lock candidates from starting at least 60 minutes before a lockout
              const offset = Math.max(
                60,
                (assessment && assessment.estimatedTime()) || 0,
              );
              return now < startTime - minute * offset;
            }
          }
          return true;
        },
        checkLogin(assessment) {
          return precheck(() => svc.canLogin(assessment));
        },
        isStaff() {
          const account = accountService.get();
          return !!(
            account.user &&
            account.user.hasRole &&
            account.user.hasRole('staff')
          );
        },
        warning() {
          const active = svc.active;
          if (active) {
            // set up a timer for alerts if there's an active event
            const now = Date.now();
            const then = active.startTime.getTime();
            const time = $filter('date')(active.startTime, 'shortTime');
            if (then - WARNING_TIMES.DANGER2 < now) {
              // 5 minutes before lockout show danger again
              return {
                mode: 'danger',
                message: `WARNING: System maintenance is starting at ${time}`,
              };
            } else if (then - WARNING_TIMES.DANGER < now) {
              // 15 minutes before lockout show danger
              return {
                mode: 'danger',
                message: `WARNING: System maintenance will begin soon, at ${time}`,
              };
            } else if (then - WARNING_TIMES.WARNING < now) {
              // 1 hour before lockout show a warning
              return {
                mode: 'warning',
                message: `System maintenance will begin at ${time}`,
              };
            } else if (then - WARNING_TIMES.INFO < now) {
              // 4 hours before lockout, show info
              return {
                mode: 'info',
                message: `System maintenance is scheduled for ${time}`,
              };
            }
          }
        },
        init() {
          svc
            .checkAccess()
            .then(() => {
              const active = svc.active;
              if (active) {
                // set up timers for alerts if there's an active event
                const now = Date.now();
                const then = active.startTime.getTime();
                // 4 hours before lockout, show info
                _showBannerIn(then - WARNING_TIMES.INFO - now);
                // 1 hour before lockout show a warning
                _showBannerIn(then - WARNING_TIMES.WARNING - now);
                // 15 minutes before lockout show danger
                _showBannerIn(then - WARNING_TIMES.DANGER - now);
                // 5 minutes before lockout show danger again
                _showBannerIn(then - WARNING_TIMES.DANGER2 - now);
              }
            })
            .catch(() => svc.goToError());
        },
        errorState() {
          return ['error', { maintenanceEvent: svc.active }];
        },
        goToError() {
          return $state.go(...svc.errorState());
        },
        showDetails() {
          const active = svc.active;
          if (!active) return;
          const startTime = $filter('date')(active.startTime, 'medium');
          dialogService.alert(`\
<h3>${active.reason || 'The system is undergoing scheduled maintenance'}</h3>\
${active.description}\
<p>Work is scheduled to start at <strong>${startTime}</strong>, and is expected to take <strong>${active.estimatedDowntimeMinutes} minutes</strong>.</p>`);
        },
      };

      function _showBannerIn(delay) {
        const showBanner = () => {
          let displayService;
          if ($injector.has('productMessageBannerService')) {
            displayService = $injector.get('productMessageBannerService');
          } else if ($injector.has('appAlert')) {
            displayService = $injector.get('appAlert');
          }
          const warningText = svc.warning();
          if (displayService && warningText) {
            const { mode, message } = warningText;
            const props = {
              linkAction: svc.showDetails,
              linkTitle: 'Learn More',
            };
            if (mode === 'danger') {
              props.closeable = props.showFor = false;
            }
            displayService['show' + ucFirst(mode)](message, props);
          }
        };
        if (delay > 0) {
          timerHelper.longTimeout(showBanner, delay);
        } else {
          showBanner();
        }
      }

      return svc;
    },
  );
